import { SxProps, Theme } from "@mui/material";
import profileTokens, { profileResponsiveTokens } from "./MyProfile.tokens";
import { PHONE_SM_QUERY } from "screens/Settings/Settings.tokens";

const {
  card,
  heading,
  description,
  avatar,
  smallButton,
  field,
  roleChip,
  primaryButton,
  signature,
  row,
  toggle,
  danger,
  footer,
} = profileTokens;

const { phone, tablet } = profileResponsiveTokens;

/** Shared white card chrome (border, radius, shadow) for every block. */
const cardChrome = {
  bgcolor: "background.paper",
  border: `1px solid ${card.border}`,
  borderRadius: card.radius,
  boxShadow: card.shadow,
  boxSizing: "border-box",
} as const;

/**
 * Responsive geometry the form controls share. `xs` carries the reference's 390
 * column (with `PHONE_SM_QUERY` narrowing it to 375), `sm` the 834 tablet, and
 * `md` the untouched desktop values.
 */
const fieldHeight = {
  xs: phone.field.height.std,
  sm: tablet.field.height,
  md: field.height,
};

const fieldRadius = {
  xs: field.radius,
  sm: tablet.field.radius,
  md: field.radius,
};

const fieldFontSize = {
  xs: phone.field.fontSize.std,
  sm: tablet.field.fontSize,
  md: field.fontSize,
};

const fieldPaddingX = {
  xs: `0 ${field.paddingX}`,
  sm: `0 ${tablet.field.paddingX}`,
  md: `0 ${field.paddingX}`,
};

const fieldLabelFontSize = {
  xs: phone.field.labelFontSize.std,
  sm: tablet.field.labelFontSize,
  md: field.labelFontSize,
};

/** Single-line card chrome (Newsletter, Close your account, MFA…). */
const rowCardBase = {
  ...cardChrome,
  p: {
    xs: phone.card.rowPadding.std,
    sm: tablet.card.rowPadding,
    md: card.rowPadding,
  },
  display: "flex",
  // The phone reference stacks a row card's text over its control.
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "flex-start", sm: "center" },
  gap: { xs: phone.card.rowGap, sm: tablet.card.rowGap, md: card.rowGap },
  [PHONE_SM_QUERY]: { padding: phone.card.rowPadding.sm },
} as const;

const myProfileStyles = {
  /** "Profile Details" + "Signatures & Initials" cards. */
  formCard: {
    ...cardChrome,
    p: { xs: phone.card.padding.std, sm: tablet.card.padding, md: card.padding },
    [PHONE_SM_QUERY]: { padding: phone.card.padding.sm },
  } as SxProps<Theme>,

  rowCard: rowCardBase as SxProps<Theme>,

  /**
   * Newsletter opt-in. Both small references run My Profile as three cards —
   * details, signatures, close account — so this one is desktop-only there.
   */
  newsletterCard: {
    ...rowCardBase,
    display: { xs: "none", md: "flex" },
  } as SxProps<Theme>,

  /** Close-account card: same row chrome, red-tinted border, no elevation. */
  dangerRowCard: {
    ...cardChrome,
    borderColor: danger.cardBorder,
    boxShadow: "none",
    p: {
      xs: phone.card.rowPadding.std,
      sm: tablet.card.rowPadding,
      md: card.rowPadding,
    },
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "flex-start", sm: "center" },
    gap: { xs: phone.card.rowGap, sm: tablet.card.rowGap, md: card.rowGap },
    [PHONE_SM_QUERY]: { padding: phone.card.rowPadding.sm },
  } as SxProps<Theme>,

  cardHeader: {
    display: "flex",
    flexDirection: "column",
    gap: heading.gap,
  } as SxProps<Theme>,

  cardTitle: {
    color: heading.color,
    fontSize: {
      xs: phone.heading.fontSize.std,
      sm: tablet.heading.fontSize,
      md: heading.fontSize,
    },
    fontWeight: heading.fontWeight,
    letterSpacing: heading.letterSpacing,
    lineHeight: heading.lineHeight,
    [PHONE_SM_QUERY]: { fontSize: phone.heading.fontSize.sm },
  } as SxProps<Theme>,

  cardDescription: {
    color: description.color,
    fontSize: {
      xs: phone.description.fontSize.std,
      sm: tablet.description.fontSize,
      md: description.fontSize,
    },
    lineHeight: description.lineHeight,
    [PHONE_SM_QUERY]: { fontSize: phone.description.fontSize.sm },
  } as SxProps<Theme>,

  profileCardBody: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "flex-start" },
    gap: {
      xs: phone.card.gap.std,
      sm: tablet.card.columnGap,
      md: card.columnGap,
    },
    [PHONE_SM_QUERY]: { gap: phone.card.gap.sm },
  } as SxProps<Theme>,

  /**
   * Avatar plus its "Change" / "Remove" buttons. The reference stands them in a
   * column beside the fields on tablet and desktop, and lays them out as one
   * left-aligned row above the fields on the phone.
   */
  avatarColumn: {
    width: { xs: "100%", sm: tablet.avatar.columnWidth, md: avatar.columnWidth },
    flexShrink: 0,
    display: "flex",
    flexDirection: { xs: "row", sm: "column" },
    alignItems: "center",
    justifyContent: { xs: "flex-start", sm: "center", md: "flex-start" },
    gap: {
      xs: phone.avatar.rowGap.std,
      sm: tablet.avatar.gap,
      md: avatar.gap,
    },
    [PHONE_SM_QUERY]: { gap: phone.avatar.rowGap.sm },
  } as SxProps<Theme>,

  avatar: {
    width: { xs: phone.avatar.size.std, sm: tablet.avatar.size, md: avatar.size },
    height: {
      xs: phone.avatar.size.std,
      sm: tablet.avatar.size,
      md: avatar.size,
    },
    flexShrink: 0,
    // Both small references measure the ring *inside* the given size; the
    // desktop one was built against MUI's default box, so it keeps it.
    boxSizing: { xs: "border-box", md: "content-box" },
    bgcolor: avatar.bg,
    border: `1px solid ${avatar.border}`,
    color: avatar.color,
    fontSize: {
      xs: phone.avatar.fontSize.std,
      sm: tablet.avatar.fontSize,
      md: avatar.fontSize,
    },
    fontWeight: avatar.fontWeight,
    [PHONE_SM_QUERY]: {
      width: phone.avatar.size.sm,
      height: phone.avatar.size.sm,
      fontSize: phone.avatar.fontSize.sm,
    },
  } as SxProps<Theme>,

  avatarActions: {
    display: "flex",
    gap: { xs: phone.avatar.buttonGap, md: avatar.buttonGap },
  } as SxProps<Theme>,

  smallButton: {
    minWidth: 0,
    height: {
      xs: phone.smallButton.height.std,
      sm: tablet.smallButton.height,
      md: smallButton.height,
    },
    px: {
      xs: phone.smallButton.paddingX.std,
      sm: tablet.smallButton.paddingX,
      md: smallButton.paddingX,
    },
    bgcolor: smallButton.bg,
    border: `1px solid ${smallButton.border}`,
    borderRadius: {
      xs: smallButton.radius,
      sm: tablet.smallButton.radius,
      md: smallButton.radius,
    },
    fontSize: {
      xs: phone.smallButton.fontSize.std,
      sm: tablet.smallButton.fontSize,
      md: smallButton.fontSize,
    },
    fontWeight: smallButton.fontWeight,
    lineHeight: "normal",
    color: smallButton.color,
    textTransform: "none",
    "&:hover": {
      bgcolor: smallButton.bg,
      borderColor: smallButton.hoverBorder,
    },
    [PHONE_SM_QUERY]: {
      height: phone.smallButton.height.sm,
      paddingLeft: phone.smallButton.paddingX.sm,
      paddingRight: phone.smallButton.paddingX.sm,
      fontSize: phone.smallButton.fontSize.sm,
    },
  } as SxProps<Theme>,

  smallDangerButton: {
    minWidth: 0,
    // The tablet reference keeps only "Change" under the 100px avatar column —
    // there is no room for a second button, and removing a photo is also
    // reachable from the change dialog.
    display: { sm: "none", md: "inline-flex" },
    height: {
      xs: phone.smallButton.height.std,
      md: smallButton.height,
    },
    px: {
      xs: phone.smallButton.paddingX.std,
      md: smallButton.paddingX,
    },
    bgcolor: smallButton.bg,
    border: `1px solid ${smallButton.border}`,
    borderRadius: smallButton.radius,
    fontSize: {
      xs: phone.smallButton.fontSize.std,
      md: smallButton.fontSize,
    },
    fontWeight: smallButton.fontWeight,
    lineHeight: "normal",
    color: smallButton.dangerColor,
    textTransform: "none",
    "&:hover": {
      bgcolor: smallButton.bg,
      borderColor: smallButton.dangerHoverBorder,
    },
    [PHONE_SM_QUERY]: {
      height: phone.smallButton.height.sm,
      paddingLeft: phone.smallButton.paddingX.sm,
      paddingRight: phone.smallButton.paddingX.sm,
      fontSize: phone.smallButton.fontSize.sm,
    },
  } as SxProps<Theme>,

  detailsColumn: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: {
      xs: phone.card.gap.std,
      sm: tablet.heading.bodyGap,
      md: heading.bodyGap,
    },
    [PHONE_SM_QUERY]: { gap: phone.card.gap.sm },
  } as SxProps<Theme>,

  fieldGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      /*
        The 834 reference pairs Name/Email and Phone/Role. Asking for two fixed
        columns from `sm` up would also pair them at 600, where the details
        column is only ~170px wide and the phone number collapses to a single
        character; `auto-fit` keeps the pairing exactly where it fits.
      */
      sm: "repeat(auto-fit, minmax(190px, 1fr))",
      md: "minmax(0, 1fr) minmax(0, 1fr)",
    },
    rowGap: {
      xs: phone.card.gap.std,
      sm: tablet.field.rowGap,
      md: field.rowGap,
    },
    columnGap: { sm: tablet.field.columnGap, md: field.columnGap },
    [PHONE_SM_QUERY]: { rowGap: phone.card.gap.sm },
  } as SxProps<Theme>,

  fieldLabel: {
    color: field.labelColor,
    fontSize: fieldLabelFontSize,
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
    ml: 0,
    "& .MuiFormLabel-asterisk": {
      color: field.requiredColor,
    },
    [PHONE_SM_QUERY]: { fontSize: phone.field.labelFontSize.sm },
  } as SxProps<Theme>,

  mutedFieldLabel: {
    color: field.labelMutedColor,
    fontSize: fieldLabelFontSize,
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
    ml: 0,
    [PHONE_SM_QUERY]: { fontSize: phone.field.labelFontSize.sm },
  } as SxProps<Theme>,

  textField: {
    "& .MuiOutlinedInput-root": {
      height: fieldHeight,
      p: 0,
      borderRadius: fieldRadius,
      bgcolor: field.bg,
      [PHONE_SM_QUERY]: { height: phone.field.height.sm },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
        borderWidth: "1px",
      },
      "&:hover": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
      },
      "&.Mui-focused": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: field.focusBorder,
          borderWidth: "1px",
        },
      },
      "&.Mui-disabled": {
        bgcolor: field.disabledBg,
        borderColor: field.disabledBorder,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: field.disabledBorder,
        },
      },
      // Keep the validation outline visible — the base rules above are more
      // specific than MUI's own error styling.
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "error.main",
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      p: fieldPaddingX,
      fontSize: fieldFontSize,
      fontWeight: field.fontWeight,
      color: field.color,
      [PHONE_SM_QUERY]: { fontSize: phone.field.fontSize.sm },
      "&::placeholder": {
        color: field.placeholder,
        opacity: 1,
        fontSize: fieldFontSize,
        fontWeight: field.fontWeight,
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: field.disabledColor,
        color: field.disabledColor,
      },
    },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "4px",
      fontSize: field.errorFontSize,
    },
  } as SxProps<Theme>,

  /** Number half of the phone control — same box as every other field. */
  phoneNumberField: {
    "& .MuiOutlinedInput-root": {
      height: fieldHeight,
      borderRadius: fieldRadius,
      [PHONE_SM_QUERY]: { height: phone.field.height.sm },
    },
    "& .MuiOutlinedInput-input": {
      p: fieldPaddingX,
      fontSize: fieldFontSize,
      [PHONE_SM_QUERY]: { fontSize: phone.field.fontSize.sm },
      "&::placeholder": { fontSize: fieldFontSize },
    },
  } as SxProps<Theme>,

  /** Read-only "Role" cell — matches the field height beside it. */
  roleValueRow: {
    display: "flex",
    alignItems: "center",
    gap: roleChip.rowGap,
    height: { sm: tablet.field.height, md: field.height },
  } as SxProps<Theme>,

  /**
   * The phone reference drops the "Role" label and the note beside the chip —
   * the chip names the role itself there, so both would be redundant.
   */
  roleLabelRow: {
    display: { xs: "none", sm: "block" },
  } as SxProps<Theme>,

  roleChip: {
    display: "inline-flex",
    alignItems: "center",
    // The gap only separates the shield from the label, and the shield is
    // desktop-only — below it the chip is a single run of words.
    gap: { xs: 0, md: roleChip.gap },
    p: {
      xs: phone.roleChip.padding.std,
      sm: tablet.roleChip.padding,
      md: roleChip.padding,
    },
    borderRadius: roleChip.radius,
    bgcolor: roleChip.bg,
    border: `1px solid ${roleChip.border}`,
    color: roleChip.color,
    fontSize: {
      xs: phone.roleChip.fontSize.std,
      sm: tablet.roleChip.fontSize,
      md: roleChip.fontSize,
    },
    fontWeight: roleChip.fontWeight,
    lineHeight: "normal",
    textTransform: "capitalize",
    whiteSpace: "nowrap",
    [PHONE_SM_QUERY]: {
      padding: phone.roleChip.padding.sm,
      fontSize: phone.roleChip.fontSize.sm,
    },
  } as SxProps<Theme>,

  /**
   * The phone reference drops the "Role" label, so the chip reads "Admin role"
   * there and names the field itself. Lower-cased explicitly: the chip
   * capitalizes the role value beside it.
   */
  roleChipSuffix: {
    display: { xs: "inline", sm: "none" },
    textTransform: "none",
    ml: "3.5px",
  } as SxProps<Theme>,

  /** Shield glyph inside the chip — dropped below the desktop reference. */
  roleChipIcon: {
    display: { xs: "none", md: "block" },
    width: roleChip.iconSize,
    height: roleChip.iconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Only the desktop reference has room for the note beside the chip. */
  roleNote: {
    display: { xs: "none", md: "inline" },
    fontSize: roleChip.noteFontSize,
    color: roleChip.noteColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** Both small references left-align the primary action; desktop trails it. */
  submitRow: {
    display: "flex",
    justifyContent: { xs: "flex-start", md: "flex-end" },
  } as SxProps<Theme>,

  /**
   * The two small references run the primary action shorter and tighter than
   * the global `MuiButton` chrome. `md` restates that chrome verbatim so the
   * desktop button is left exactly as the theme drew it.
   */
  submitButton: {
    height: {
      xs: phone.primaryButton.height.std,
      sm: tablet.primaryButton.height,
      md: primaryButton.height,
    },
    px: {
      xs: phone.primaryButton.paddingX.std,
      sm: tablet.primaryButton.paddingX,
      md: primaryButton.paddingX,
    },
    borderRadius: {
      xs: field.radius,
      sm: tablet.primaryButton.radius,
      md: primaryButton.radius,
    },
    fontSize: {
      xs: phone.primaryButton.fontSize.std,
      sm: tablet.primaryButton.fontSize,
      md: primaryButton.fontSize,
    },
    [PHONE_SM_QUERY]: {
      height: phone.primaryButton.height.sm,
      paddingLeft: phone.primaryButton.paddingX.sm,
      paddingRight: phone.primaryButton.paddingX.sm,
      fontSize: phone.primaryButton.fontSize.sm,
    },
  } as SxProps<Theme>,

  signatureRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: signature.rowGap,
    mt: {
      xs: phone.signature.rowMarginTop,
      sm: tablet.signature.rowMarginTop,
      md: signature.rowMarginTop,
    },
  } as SxProps<Theme>,

  signatureColumn: (width: number, tabletWidth: number) =>
    ({
      display: "flex",
      flexDirection: "column",
      gap: signature.labelGap,
      width: { xs: "100%", sm: tabletWidth, md: width },
      maxWidth: "100%",
    }) as SxProps<Theme>,

  /**
   * Both small references show the tiles alone — the card heading names them
   * and each tile carries its own accessible label.
   */
  signatureLabel: {
    display: { xs: "none", md: "block" },
    fontSize: signature.labelFontSize,
    fontWeight: signature.labelFontWeight,
    letterSpacing: signature.labelLetterSpacing,
    color: signature.labelColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  signatureTile: {
    position: "relative",
    height: {
      xs: phone.signature.tileHeight.std,
      sm: tablet.signature.tileHeight,
      md: signature.tileHeight,
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: signature.tileBg,
    border: `1px solid ${signature.tileBorder}`,
    borderRadius: signature.tileRadius,
    cursor: "pointer",
    [PHONE_SM_QUERY]: { height: phone.signature.tileHeight.sm },
    "&:hover": { borderColor: signature.tileHoverBorder },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  signatureTileEmpty: {
    position: "relative",
    width: "100%",
    height: {
      xs: phone.signature.tileHeight.std,
      sm: tablet.signature.tileHeight,
      md: signature.tileHeight,
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: signature.emptyGap,
    bgcolor: signature.tileBg,
    border: `${signature.emptyBorderWidth} dashed ${signature.emptyBorder}`,
    borderRadius: signature.tileRadius,
    cursor: "pointer",
    textTransform: "none",
    p: 0,
    [PHONE_SM_QUERY]: { height: phone.signature.tileHeight.sm },
    "&:hover": {
      bgcolor: signature.tileBg,
      borderColor: signature.tileHoverBorder,
    },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  signatureBaseline: {
    position: "absolute",
    left: signature.baselineInset,
    right: signature.baselineInset,
    bottom: signature.baselineBottom,
    borderBottom: `${signature.baselineWidth} solid ${signature.baselineColor}`,
  } as SxProps<Theme>,

  signatureEditButton: {
    position: "absolute",
    top: signature.editOffset,
    right: signature.editOffset,
    width: signature.editSize,
    height: signature.editSize,
    borderRadius: "50%",
    bgcolor: signature.editBg,
    border: `1px solid ${signature.editBorder}`,
    color: signature.editIconColor,
    "&:hover": { bgcolor: signature.editBg },
  } as SxProps<Theme>,

  signatureEditIcon: {
    width: signature.editIconSize,
    height: signature.editIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  signatureEmptyPlus: {
    fontSize: signature.emptyPlusFontSize,
    fontWeight: 400,
    lineHeight: 1,
    color: signature.emptyColor,
  } as SxProps<Theme>,

  signatureEmptyLabel: {
    fontSize: signature.emptyLabelFontSize,
    fontWeight: signature.emptyLabelFontWeight,
    lineHeight: "normal",
    color: signature.emptyColor,
  } as SxProps<Theme>,

  rowText: {
    display: "flex",
    flexDirection: "column",
    gap: row.gap,
    minWidth: 0,
  } as SxProps<Theme>,

  /** Loading variant of `rowText` — grows so the placeholder lines fill the row. */
  skeletonRowText: {
    display: "flex",
    flexDirection: "column",
    gap: row.gap,
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  rowTitle: {
    color: row.titleColor,
    fontSize: {
      xs: phone.row.titleFontSize.std,
      sm: tablet.row.titleFontSize,
      md: row.titleFontSize,
    },
    fontWeight: row.titleFontWeight,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.row.titleFontSize.sm },
  } as SxProps<Theme>,

  toggleTrack: (checked: boolean) =>
    ({
      position: "relative",
      ml: { xs: 0, sm: "auto" },
      flexShrink: 0,
      width: toggle.width,
      height: toggle.height,
      p: 0,
      borderRadius: "999px",
      bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      transition: toggle.transition,
      "&:hover": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "2px",
      },
    }) as SxProps<Theme>,

  toggleKnob: (checked: boolean) =>
    ({
      position: "absolute",
      top: toggle.knobTop,
      left: 0,
      width: toggle.knobSize,
      height: toggle.knobSize,
      borderRadius: "50%",
      bgcolor: toggle.knobBg,
      boxShadow: toggle.knobShadow,
      transform: checked ? toggle.knobOffsetOn : toggle.knobOffsetOff,
      transition: toggle.knobTransition,
    }) as SxProps<Theme>,

  dangerButton: {
    ml: { xs: 0, sm: "auto" },
    flexShrink: 0,
    height: {
      xs: phone.danger.buttonHeight.std,
      sm: tablet.danger.buttonHeight,
      md: danger.buttonHeight,
    },
    px: {
      xs: phone.danger.buttonPaddingX.std,
      sm: tablet.danger.buttonPaddingX,
      md: danger.buttonPaddingX,
    },
    bgcolor: "background.paper",
    border: `1px solid ${danger.buttonBorder}`,
    borderRadius: danger.buttonRadius,
    color: danger.buttonColor,
    fontSize: {
      xs: phone.danger.buttonFontSize.std,
      sm: tablet.danger.buttonFontSize,
      md: danger.buttonFontSize,
    },
    fontWeight: danger.buttonFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    [PHONE_SM_QUERY]: {
      height: phone.danger.buttonHeight.sm,
      paddingLeft: phone.danger.buttonPaddingX.sm,
      paddingRight: phone.danger.buttonPaddingX.sm,
      fontSize: phone.danger.buttonFontSize.sm,
    },
    "&:hover": {
      bgcolor: danger.buttonHoverBg,
      borderColor: danger.buttonHoverBorder,
    },
  } as SxProps<Theme>,

  /** Terms/privacy line — desktop-only, like the newsletter card above it. */
  footerText: {
    display: { xs: "none", md: "block" },
    px: footer.paddingX,
    fontSize: footer.fontSize,
    lineHeight: footer.lineHeight,
    color: footer.color,
  } as SxProps<Theme>,

  footerLink: {
    color: footer.linkColor,
    textDecoration: "underline",
    "&:hover": { color: footer.linkHoverColor },
  } as SxProps<Theme>,
};

export default myProfileStyles;
